import { useEffect, useState } from 'react'
import { Drawer, Descriptions, Tabs, Tag, Empty, Result, Button, Spin, Typography, Timeline } from 'antd'
// moment liegt global (siehe src/i18n.js), wie in den uebrigen Seiten dieses Portals.
import { useTranslation } from 'react-i18next'
import { apiFetch } from '../../api'
import { formatStatus } from '../../utils/serviceStatus'

/**
 * HOR-75 / HOR-74: Kundendetails und Historie in der Benutzerverwaltung.
 *
 * Bis hierhin kam ein Admin nur ueber Impersonate an die Daten eines Kunden -
 * also nur, indem er dessen App als dieser Nutzer oeffnete. Hier stehen
 * Stammdaten, Uhren und der chronologische Verlauf aller Vorgaenge direkt im
 * Portal.
 *
 * Die Daten kommen in einem Aufruf von GET /user/:id (Admin-only) mit
 * Uhren -> Vorgaenge/Verifizierung als Relationen.
 */

const na = value => (value === null || value === undefined || String(value).trim() === '' ? i18n('common.na') : value)

const formatDate = value => (value && moment(value).isValid() ? moment(value).format('L LT') : i18n('common.na'))

const watchLabel = watch => {
  const reference = watch.referenceNumber || {}
  const parts = [watch.make || reference.make, watch.model || reference.model].filter(Boolean)

  return parts.length ? parts.join(' ') : i18n('users.detail.watchWithoutName', { id: watch.id })
}

export default function UserDetailDrawer({ userId, open, onClose }) {
  useTranslation()

  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [user, setUser] = useState(null)
  const [reloadKey, setReloadKey] = useState(0)

  useEffect(() => {
    if (!open || !userId) return

    let cancelled = false

    setLoading(true)
    setError(null)

    apiFetch(`/user/${userId}`).then(result => {
      // Der Drawer kann waehrend des Requests geschlossen oder auf einen anderen
      // Kunden umgeschaltet werden - dann darf die alte Antwort nichts mehr setzen.
      if (cancelled) return

      setLoading(false)

      if (result.errorType === 'unauthorized') return // apiFetch hat bereits umgeleitet

      if (!result.ok) {
        setError(result.status === 404 ? 'notFound' : result.errorType)
        return
      }

      setUser(result.data)
    })

    return () => {
      cancelled = true
    }
  }, [userId, open, reloadKey])

  const watches = Array.isArray(user?.watches) ? user.watches : []

  // HOR-74: Alle Vorgaenge des Kunden ueber alle Uhren hinweg, juengster zuerst.
  const services = watches.flatMap(watch => (watch.services || []).map(service => ({ ...service, watch }))).sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))

  const verifications = watches.filter(watch => watch.verification).map(watch => ({ ...watch.verification, watch }))

  const masterData = user && (
    <Descriptions
      bordered
      size="small"
      column={{ xs: 1, sm: 1, md: 2 }}
      items={[
        { key: 'email', label: i18n('users.columns.email'), children: na(user.email) },
        { key: 'name', label: i18n('users.columns.username'), children: na(user.name) },
        {
          key: 'role',
          label: i18n('users.columns.role'),
          children: <Tag color={user.role === 'administrator' ? 'gold' : 'default'}>{i18n(`users.roles.${user.role}`)}</Tag>,
        },
        { key: 'language', label: i18n('users.detail.language'), children: na(user.language) },
        { key: 'createdAt', label: i18n('users.columns.registered'), children: formatDate(user.createdAt) },
        {
          key: 'lastLoginAt',
          label: i18n('users.columns.lastLogin'),
          children: user.lastLoginAt ? formatDate(user.lastLoginAt) : i18n('users.columns.never'),
        },
        {
          key: 'address',
          label: i18n('users.detail.returnAddress'),
          span: 2,
          children: [user.receiver, user.street, [user.zipCode, user.city].filter(Boolean).join(' '), user.country].filter(Boolean).join(', ') || i18n('common.na'),
        },
        {
          key: 'invoiceAddress',
          label: i18n('users.detail.invoiceAddress'),
          span: 2,
          children:
            [user.invoiceReceiver, user.invoiceStreet, [user.invoiceZipCode, user.invoiceCity].filter(Boolean).join(' '), user.invoiceCountry]
              .filter(Boolean)
              .join(', ') || i18n('common.na'),
        },
      ]}
    />
  )

  const watchesTab = watches.length ? (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      {watches.map(watch => (
        <Descriptions
          key={watch.id}
          bordered
          size="small"
          column={{ xs: 1, sm: 2 }}
          title={watchLabel(watch)}
          items={[
            { key: 'number', label: i18n('users.detail.referenceNumber'), children: na(watch.number || watch.referenceNumber?.number) },
            { key: 'serial', label: i18n('users.detail.serialNumber'), children: watch.serialNumberUnknown ? i18n('common.unknown') : na(watch.serialNumber) },
            { key: 'createdAt', label: i18n('users.detail.addedAt'), children: formatDate(watch.createdAt) },
            {
              key: 'services',
              label: i18n('users.detail.serviceCount'),
              children: (watch.services || []).length,
            },
          ]}
        />
      ))}
    </div>
  ) : (
    <Empty description={i18n('users.detail.noWatches')} />
  )

  const servicesTab = services.length ? (
    <Timeline
      items={services.map(service => ({
        color: formatStatus(service.status).color,
        children: (
          <div>
            <Typography.Text strong>{service.number || i18n('users.detail.serviceWithoutNumber', { id: service.id })}</Typography.Text>
            <div>
              <Tag color={formatStatus(service.status).color}>{formatStatus(service.status).title}</Tag>
              <Typography.Text type="secondary">{watchLabel(service.watch)}</Typography.Text>
            </div>
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              {i18n('users.detail.createdAt', { date: formatDate(service.createdAt) })}
              {service.completedAt ? ` · ${i18n('users.detail.completedAt', { date: formatDate(service.completedAt) })}` : ''}
            </Typography.Text>
          </div>
        ),
      }))}
    />
  ) : (
    <Empty description={i18n('users.detail.noServices')} />
  )

  const verificationsTab = verifications.length ? (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      {verifications.map(verification => (
        <Descriptions
          key={verification.id}
          bordered
          size="small"
          column={{ xs: 1, sm: 2 }}
          title={watchLabel(verification.watch)}
          items={[
            {
              key: 'status',
              label: i18n('users.detail.verificationStatus'),
              children: <Tag>{i18n(`verification.status.${verification.status}`, { defaultValue: verification.status })}</Tag>,
            },
            { key: 'createdAt', label: i18n('users.detail.requestedAt'), children: formatDate(verification.createdAt) },
            {
              key: 'verifiedDate',
              label: i18n('users.detail.verifiedAt'),
              children: verification.verifiedDate ? formatDate(verification.verifiedDate) : i18n('common.na'),
            },
          ]}
        />
      ))}
    </div>
  ) : (
    <Empty description={i18n('users.detail.noVerifications')} />
  )

  return (
    <Drawer
      open={open}
      onClose={onClose}
      width={860}
      destroyOnClose
      title={user ? i18n('users.detail.title', { name: user.name || user.email }) : i18n('users.detail.titleFallback')}
    >
      {loading && (
        <div style={{ display: 'flex', justifyContent: 'center', padding: 48 }}>
          <Spin />
        </div>
      )}

      {!loading && error && (
        <Result
          status={error === 'notFound' ? '404' : error === 'forbidden' ? '403' : error === 'network' ? 'warning' : 'error'}
          title={
            error === 'notFound'
              ? i18n('errors.notFound')
              : error === 'forbidden'
                ? i18n('errors.noPermission')
                : error === 'network'
                  ? i18n('errors.network')
                  : i18n('errors.loadFailed')
          }
          extra={
            // Ein 403 wird durch Wiederholen nicht besser (Muster aus HOR-1).
            error !== 'forbidden' &&
            error !== 'notFound' && (
              <Button type="primary" onClick={() => setReloadKey(key => key + 1)}>
                {i18n('common.retry')}
              </Button>
            )
          }
        />
      )}

      {!loading && !error && user && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
          {masterData}
          <Tabs
            items={[
              { key: 'services', label: i18n('users.detail.tabs.services', { count: services.length }), children: servicesTab },
              { key: 'watches', label: i18n('users.detail.tabs.watches', { count: watches.length }), children: watchesTab },
              { key: 'verifications', label: i18n('users.detail.tabs.verifications', { count: verifications.length }), children: verificationsTab },
            ]}
          />
        </div>
      )}
    </Drawer>
  )
}
